var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __pow = Math.pow;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/socket-as-fetch.ts
import { subscribeToConfig, LeagueConfigError } from "@leagueplatform/config";

// src/fetch-as-promise.ts
import {
  LeagueSocketAsPromised
} from "@leagueplatform/league-socket";
import * as uuid from "uuid";
import {
  captureError,
  finishSpan,
  startSpanOnActiveTransaction,
  combineErrorContexts,
  captureMessage
} from "@leagueplatform/observability";
import { isLoginRequiredError } from "@leagueplatform/auth";

// src/util/get-thrown-error-message.ts
function getThrownErrorMessage(error) {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

// src/util/get-user-configure-connection.ts
import jstz from "jstz";
import { DEFAULT_LOCALE, getCurrentLocale } from "@leagueplatform/locales";
function getUserConfigureConnection() {
  const locale = getCurrentLocale(DEFAULT_LOCALE);
  return {
    message_type: "configure_connection",
    info: { tz_name: jstz.determine().name(), client_platform: "web", locale }
  };
}

// src/fetch-as-promise.ts
function isErrorMessage(message) {
  var _a;
  if (!message)
    return true;
  if (message.message_type === "fail")
    return true;
  if (message.message_type === "server_error")
    return true;
  if (((_a = message == null ? void 0 : message.info) == null ? void 0 : _a.code) === "invalid_request")
    return true;
  return false;
}
var NORMAL_CLOSURE_CODE = 1e3;
var MAX_RECONNECTION_RETRIES = 3;
var BASE_DELAY_MILIS = 350;
var RECONNECTION_COUNTER_RESET_MILIS = 3e4;
var RECONNECT_REQUESTED_MESSAGE_TYPE = "reconnect_requested";
var _options, _initPromise, _numReconnectionAttempts, _onOpenTimeout, _init, init_fn;
var FetchAsPromise = class extends LeagueSocketAsPromised {
  constructor(url, options = { anonymous: false }) {
    super(url);
    __privateAdd(this, _init);
    __privateAdd(this, _options, void 0);
    /**
     * This promise will be created on the first call to `fetch` by setting it to
     * the promise returned by `init`. That call to `fetch`, as well as every subsequent
     * call to it, will then await this promise before proceeding to send messages.
     */
    __privateAdd(this, _initPromise, void 0);
    __privateAdd(this, _numReconnectionAttempts, 0);
    /**
     * When the connection opens, we count back from RECONNECTION_COUNTER_RESET_MILIS
     * and then reset the reconnection retries counter. But when the connection closes
     * unexpectedly, we want to cancel this reset. So, we store the timeout
     * here.
     */
    __privateAdd(this, _onOpenTimeout, void 0);
    /**
     * Defined as a public class field arrow function instead of a public class method
     * so that we get the automatic `this`-binding, since this function is passed
     * directly to `window.addEventListener`
     */
    this.unload = () => __async(this, null, function* () {
      yield this.close(NORMAL_CLOSURE_CODE);
      if (typeof window !== void 0 && typeof window.addEventListener === "function") {
        window.removeEventListener("unload", this.unload);
      }
      this.onClose.removeAllListeners();
      this.onError.removeAllListeners();
      this.onUnpackedMessage.removeAllListeners();
      this.onOpen.removeAllListeners();
    });
    __privateSet(this, _options, options);
    this.onOpen.addListener(() => {
      if (__privateGet(this, _onOpenTimeout) !== void 0) {
        window.clearTimeout(__privateGet(this, _onOpenTimeout));
      }
      __privateSet(this, _onOpenTimeout, window.setTimeout(() => {
        __privateSet(this, _numReconnectionAttempts, 0);
      }, RECONNECTION_COUNTER_RESET_MILIS));
    });
    this.onUnpackedMessage.addListener((event) => __async(this, null, function* () {
      if (event.message_type === RECONNECT_REQUESTED_MESSAGE_TYPE) {
        yield this.close();
        __privateSet(this, _initPromise, __privateMethod(this, _init, init_fn).call(this));
      }
    }));
    this.onClose.addListener((event) => {
      const { code, reason, wasClean } = event;
      const context = {
        "WS Close Event": {
          code,
          reason,
          wasClean
        }
      };
      if (code !== NORMAL_CLOSURE_CODE) {
        window.clearTimeout(__privateGet(this, _onOpenTimeout));
        if (__privateGet(this, _numReconnectionAttempts) <= MAX_RECONNECTION_RETRIES) {
          context["WS Close Event"].reconnectionAttemptNum = __privateGet(this, _numReconnectionAttempts) + 1;
          captureMessage(
            "WebSocket connection closed unexpectedly. Attempting to reconnect",
            {
              severityLevel: "warning",
              context
            }
          );
          const delay = BASE_DELAY_MILIS * __pow(2, __privateGet(this, _numReconnectionAttempts));
          __privateSet(this, _initPromise, __privateMethod(this, _init, init_fn).call(this, delay));
          __privateGet(this, _initPromise).catch((e) => {
            if (!isLoginRequiredError(e)) {
              captureError(
                new Error(
                  `LeagueReconnectingSocket could not authenticate. ${e}`
                )
              );
            }
          });
          __privateSet(this, _numReconnectionAttempts, __privateGet(this, _numReconnectionAttempts) + 1);
        } else {
          captureError(
            new Error(
              "WebSocket connection closed unexpectedly, but we are out of retry attempts."
            ),
            {
              context
            }
          );
        }
      } else {
        captureMessage("WebSocket connection closed with a normal code.", {
          severityLevel: "info",
          context
        });
      }
    });
    this.onError.addListener(() => {
      captureError(new Error("WebSocket connection error"));
    });
    if (typeof window !== void 0 && typeof window.addEventListener === "function") {
      window.addEventListener("unload", this.unload);
    }
  }
  configureConnection() {
    return __async(this, null, function* () {
      const configMessage = getUserConfigureConnection();
      yield this.sendRequest(configMessage, {
        requestId: uuid.v4()
      });
    });
  }
  /**
   * Extend superclass with a fetch-like interface that applies a unique message
   * ID and throws errors given in the response. It also handles waiting for
   * the socket to open so that consumers don't have to remember to do that.
   */
  fetch(_0) {
    return __async(this, arguments, function* (message, fetchOptions = {}, observabilityTags = {}) {
      var _a, _b, _c, _d, _e, _f;
      if (!__privateGet(this, _initPromise)) {
        __privateSet(this, _initPromise, __privateMethod(this, _init, init_fn).call(this));
      }
      const span = startSpanOnActiveTransaction({
        op: "http.client",
        description: message.message_type,
        tags: __spreadValues({
          isWS: true
        }, observabilityTags)
      });
      const { errorContext: optionalErrorContext = {}, onInternalError } = fetchOptions;
      let response;
      try {
        yield __privateGet(this, _initPromise);
        if (!__privateGet(this, _options).anonymous) {
          yield this.authenticateIfNeeded();
        }
        response = yield this.sendRequest(message, {
          requestId: uuid.v4()
        });
      } catch (error) {
        if (span)
          finishSpan(span);
        const processedError = (_a = onInternalError == null ? void 0 : onInternalError(error)) != null ? _a : error;
        const errorMessage = getThrownErrorMessage(processedError);
        const fetchError = new Error(
          `{ "info": { "reason": "${errorMessage}" } }`
        );
        const fetchErrorContext = combineErrorContexts([
          { errorName: "Socket Fetch Error" },
          optionalErrorContext
        ]);
        captureError(fetchError, fetchErrorContext);
        throw fetchError;
      }
      if (span)
        finishSpan(span);
      if (isErrorMessage(response)) {
        const messageType = (_c = (_b = response.info) == null ? void 0 : _b.message) == null ? void 0 : _c.message_type;
        const fetchErrorContext = combineErrorContexts([
          {
            errorName: `Socket Fetch Response Error: ${messageType}`,
            tags: {
              messageType,
              messageId: (_e = (_d = response.info) == null ? void 0 : _d.message) == null ? void 0 : _e.message_id
            }
          },
          optionalErrorContext
        ]);
        captureError(Error((_f = response.info) == null ? void 0 : _f.reason), fetchErrorContext);
        throw Error(JSON.stringify(response));
      }
      return response == null ? void 0 : response.info;
    });
  }
};
_options = new WeakMap();
_initPromise = new WeakMap();
_numReconnectionAttempts = new WeakMap();
_onOpenTimeout = new WeakMap();
_init = new WeakSet();
init_fn = function(delay = 0) {
  return __async(this, null, function* () {
    yield new Promise((res) => {
      setTimeout(res, delay);
    });
    const span = startSpanOnActiveTransaction({
      /**
       * we set the op to the same value that Sentry's
       * built-in instrumentation sets it when it creates
       * a `fetch` span. This will allow us to query for aggregate
       * data about API-related spans that incldues both WS calls and
       * REST calls.
       */
      op: "http.client",
      description: "Initializing WebSocket connection",
      tags: {
        isWS: true,
        isAnonymous: !!__privateGet(this, _options).anonymous
      }
    });
    try {
      yield this.open();
      if (!__privateGet(this, _options).anonymous) {
        yield this.authenticate();
      }
      yield this.configureConnection();
    } catch (error) {
      if (span)
        finishSpan(span);
      throw error;
    }
    if (span)
      finishSpan(span);
  });
};

// src/subscribe-to-message.ts
var listenerMap = /* @__PURE__ */ new Map();
var subscribeToMessage = (messageType, listener) => {
  let listenerSetForThisMessageType = listenerMap.get(messageType);
  if (!listenerSetForThisMessageType) {
    listenerSetForThisMessageType = /* @__PURE__ */ new Set();
    listenerMap.set(messageType, listenerSetForThisMessageType);
  }
  listenerSetForThisMessageType.add(listener);
  return () => {
    listenerSetForThisMessageType.delete(listener);
  };
};
var callListenersWithMessage = (message) => {
  var _a;
  const { message_type: messageType, info: data } = message;
  (_a = listenerMap.get(messageType)) == null ? void 0 : _a.forEach((listener) => {
    listener(data);
  });
};

// src/socket-as-fetch.ts
var fetchAsPromise;
var fetchAsPromiseAnonymous;
var SocketAsFetch = {
  fetch: (...args) => __async(void 0, null, function* () {
    if (!fetchAsPromise) {
      throw new LeagueConfigError(
        "SocketAsFetch cannot be used without the `core.api.wsUrl` config property being set."
      );
    }
    return fetchAsPromise.fetch(...args);
  }),
  updateUserConnectionConfiguration: () => {
    if (!fetchAsPromise) {
      throw new LeagueConfigError(
        "SocketAsFetch cannot be used without the `core.api.wsUrl` config property being set."
      );
    }
    return fetchAsPromise.configureConnection();
  }
};
var SocketAsFetchAnonymous = {
  fetch: (...args) => __async(void 0, null, function* () {
    if (!fetchAsPromiseAnonymous) {
      throw new LeagueConfigError(
        "SocketAsFetchAnonymous cannot be used without the `core.api.wsUrl` config property being set."
      );
    }
    return fetchAsPromiseAnonymous.fetch(...args);
  })
};
subscribeToConfig(
  ({ config }) => config.core.api.wsUrl,
  (url) => {
    if (!url) {
      return;
    }
    if (fetchAsPromise) {
      fetchAsPromise.unload();
    }
    if (fetchAsPromiseAnonymous) {
      fetchAsPromiseAnonymous.unload();
    }
    fetchAsPromise = new FetchAsPromise(url);
    fetchAsPromiseAnonymous = new FetchAsPromise(url, {
      anonymous: true
    });
    fetchAsPromise.onUnpackedMessage.addListener((message) => {
      callListenersWithMessage(message);
    });
  }
);
export {
  SocketAsFetch,
  SocketAsFetchAnonymous,
  subscribeToMessage
};


  if (typeof window !== 'undefined' && !(typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')) {
    window.leagueSDKsPresent = window.leagueSDKsPresent || [];
    window.leagueSDKsPresent['@leagueplatform/socket-as-fetch'] = window.leagueSDKsPresent['@leagueplatform/socket-as-fetch'] || [];
    window.leagueSDKsPresent['@leagueplatform/socket-as-fetch'].push('2.23.5');

    const numInstances = window.leagueSDKsPresent['@leagueplatform/socket-as-fetch'].length

    if (numInstances > 1) {
      console.error('Multiple copies (' + numInstances + ') of the @leagueplatform/socket-as-fetch module are loaded! This can cause issues. Ensure that your project bundle only contains one copy/version of each League SDK package.');
    }
  }
  
//# sourceMappingURL=index.js.map